// Copyright 2026 Zibby Lab. Apache-2.0.
//
// AgentOpsToggle — per-app on/off switch for the in-container agent-ops
// scheduler. Shared by the cloud-app Settings tab and the solo-app detail
// body so the control reads + behaves identically across deploy modes.
//
// `onToggle(next)` performs the real flip (async; the caller hits
// Apps.setAgentOps / setSoloAgentOps, then refetches). It MUST throw on
// failure so the optimistic switch reverts and the caller's toast explains
// why. The app itself keeps running when turned off — only agent-ops'
// autonomous scheduled runs stop; `zibby app run` still works.

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import './AgentOpsToggle.css';

export default function AgentOpsToggle({ enabled, onToggle, separated = true }) {
  const [on, setOn] = useState(enabled !== false);
  const [submitting, setSubmitting] = useState(false);

  // Keep in sync when the parent refetches the app (e.g. after the flip
  // resolves, or a poll updates the row).
  useEffect(() => { setOn(enabled !== false); }, [enabled]);

  const flip = async () => {
    if (submitting) return;
    const next = !on;
    setOn(next); // optimistic
    setSubmitting(true);
    try {
      await onToggle?.(next);
    } catch {
      setOn(!next); // revert — the caller surfaces the error toast
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`agentops-toggle${separated ? ' agentops-toggle--separated' : ''}`}>
      <div className="agentops-toggle-text">
        <h3 className="agentops-toggle-title">Agent automation</h3>
        <p className="agentops-toggle-hint">
          When on, agent-ops runs its scheduled health checks + upgrades inside the
          app (needs a BYOK Claude token — without one it stays idle, it won&apos;t
          error). Turn it off to pause all autonomous agent activity; the app keeps
          running and <code>zibby app run</code> still works.
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label="Toggle agent automation"
        className={`agentops-switch${on ? ' is-on' : ''}${submitting ? ' is-busy' : ''}`}
        disabled={submitting}
        onClick={flip}
      >
        <span className="agentops-switch-knob" />
      </button>
    </div>
  );
}

AgentOpsToggle.propTypes = {
  enabled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  separated: PropTypes.bool,
};
